import { forwardRef } from 'react';
import type { ReactNode } from 'react';

export interface ListItemProps {
  icon?: ReactNode;
  title: string;
  /**
   * Secondary line under the title. `ReactNode` rather than `string` so a
   * row can put a rich status there — Settings' Email Monitoring needs
   * its colour-coded state dot beside the label, and the label belongs
   * under the title rather than beside it (YAR-230).
   */
  subtitle?: ReactNode;
  trailing?: ReactNode;
  onClick?: () => void;
  showDivider?: boolean;
  className?: string;
  /**
   * Overrides the title's colour class. Opt-in per surface: the default is
   * body copy, and screens whose frame calls for a different tone say so at
   * the call site rather than the component changing for everyone.
   */
  titleClassName?: string;
}

export const ListItem = forwardRef<HTMLDivElement, ListItemProps>(
  (
    {
      icon,
      title,
      subtitle,
      trailing,
      onClick,
      showDivider,
      className = '',
      titleClassName = 'text-foreground',
    },
    ref,
  ) => {
    return (
      <div
        ref={ref}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        onClick={onClick}
        onKeyDown={
          onClick
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClick();
                }
              }
            : undefined
        }
        className={[
          'flex items-center gap-2 px-4 py-3',
          onClick
            ? 'cursor-pointer hover:bg-surface-secondary transition-colors duration-150'
            : '',
          showDivider ? 'border-b border-border' : '',
          onClick
            ? 'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'
            : '',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {icon && (
          <div className="flex items-center justify-center w-[44px] h-[44px] shrink-0 rounded-full bg-surface-secondary p-2.5">
            {icon}
          </div>
        )}
        <div className="flex flex-col min-w-0 flex-1">
          <span
            className={`text-base font-medium leading-6 truncate ${titleClassName}`}
          >
            {title}
          </span>
          {subtitle && (
            <span className="text-sm leading-5 text-muted-foreground truncate">
              {subtitle}
            </span>
          )}
        </div>
        {trailing && <div className="shrink-0 ml-auto">{trailing}</div>}
      </div>
    );
  },
);

ListItem.displayName = 'ListItem';
